import {
  ChannelSnapshotStatusSchema,
  ThemeDetailSchema,
  ThemeListItemSchema,
  type ChannelSnapshotStatus,
  type ThemeDetail,
  type ThemeListItem,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

// Insights data layer. Three security-definer read RPCs in migration 0076
// back the surface; this file wraps them with Zod validation and snake-case
// to camelCase mapping. The Refresh button calls request_channel_snapshot
// from migration 0074 - error tokens mapped to typed strings the UI maps
// to user-facing copy at the call site.
//
// Mirrors apps/portal/src/games/recruitment-data.ts in style: plain async
// functions, query reads return T | T[] | null and warn on parse failure;
// the mutation returns { ok, data, error: ErrorToken | null }.

// --- get_game_themes --------------------------------------------------------

// PG returns snake_case; map to the camelCase shape Zod expects. Both
// the get_game_themes table-row shape and the get_theme_detail jsonb
// theme block use snake_case keys (to_jsonb(row) preserves column
// names), so we read snake_case only - a `??` fallback chain to the
// camelCase variant turns valid nulls into undefineds and fails the
// nullable() check on description / severityHint / lastMemberAddedAt.
function mapThemeRow(raw: unknown): ThemeListItem | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const channelNamesRaw = r['channel_names'];
  const channelNames = Array.isArray(channelNamesRaw) ? channelNamesRaw : [];
  // Trajectory has two shapes on the wire:
  //   - get_game_themes returns flat snake_case columns
  //     (previous_bucket, previous_classified_at).
  //   - get_theme_detail embeds a camelCase trajectory object on the
  //     theme block via jsonb_build_object.
  // Normalise both to { previousBucket, previousClassifiedAt }.
  const trajectoryRaw = r['trajectory'];
  const trajectory =
    trajectoryRaw !== undefined && trajectoryRaw !== null && typeof trajectoryRaw === 'object'
      ? trajectoryRaw
      : {
          previousBucket: r['previous_bucket'] ?? null,
          previousClassifiedAt: r['previous_classified_at'] ?? null,
        };

  const camel = {
    id: r['id'],
    title: r['title'],
    description: r['description'],
    dominantPolarity: r['dominant_polarity'],
    severityHint: r['severity_hint'],
    contentAxis: r['content_axis'],
    status: r['status'],
    pinned: r['pinned'],
    bucket: r['bucket'] ?? 'none',
    trajectory,
    firstSeenAt: r['first_seen_at'],
    earliestMentionAt: r['earliest_mention_at'] ?? null,
    lastMemberAddedAt: r['last_member_added_at'],
    memberCount:
      typeof r['member_count'] === 'string'
        ? Number(r['member_count'])
        : r['member_count'],
    channelNames,
  };
  const parsed = ThemeListItemSchema.safeParse(camel);
  if (!parsed.success) {
    console.warn('[insights] theme row parse failed', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

// --- get_game_message_count -------------------------------------------------

// Headline summary used by the Insights page. Migration 0081 widened this
// from a bare bigint to a jsonb shape carrying the counts the headline
// needs in one round trip. Returns zeros on any failure so the UI
// degrades gracefully (header reads "0 patterns detected across 0
// messages" rather than blocking the page).
export interface GameInsightsSummary {
  chatMessageCount: number;
  chatChannelCount: number;
  formSubmissionCount: number;
  totalMessageCount: number;
}

const EMPTY_SUMMARY: GameInsightsSummary = {
  chatMessageCount: 0,
  chatChannelCount: 0,
  formSubmissionCount: 0,
  totalMessageCount: 0,
};

function asCount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export async function fetchGameInsightsSummary(
  gameId: string,
): Promise<GameInsightsSummary> {
  const { data, error } = await supabase.rpc('get_game_message_count', {
    p_game_id: gameId,
  });
  if (error !== null) {
    console.warn('[insights] get_game_message_count failed:', error.message);
    return EMPTY_SUMMARY;
  }
  if (data === null || typeof data !== 'object') return EMPTY_SUMMARY;
  const r = data as Record<string, unknown>;
  return {
    chatMessageCount: asCount(r['chatMessageCount']),
    chatChannelCount: asCount(r['chatChannelCount']),
    formSubmissionCount: asCount(r['formSubmissionCount']),
    totalMessageCount: asCount(r['totalMessageCount']),
  };
}

export async function fetchGameThemes(gameId: string): Promise<ThemeListItem[]> {
  const { data, error } = await supabase.rpc('get_game_themes', {
    p_game_id: gameId,
  });
  if (error !== null) {
    console.warn('[insights] get_game_themes failed:', error.message);
    return [];
  }
  if (!Array.isArray(data)) return [];
  const themes: ThemeListItem[] = [];
  for (const row of data) {
    const mapped = mapThemeRow(row);
    if (mapped !== null) themes.push(mapped);
  }
  return themes;
}

// --- get_theme_detail -------------------------------------------------------

export async function fetchThemeDetail(themeId: string): Promise<ThemeDetail | null> {
  const { data, error } = await supabase.rpc('get_theme_detail', {
    p_theme_id: themeId,
  });
  if (error !== null) {
    console.warn('[insights] get_theme_detail failed:', error.message);
    return null;
  }
  if (data === null || typeof data !== 'object') return null;
  // The RPC returns jsonb with an already-camelCase aspects[]. The theme
  // block is the row shape with a nested trajectory object (camelCase
  // keys); mapThemeRow handles both list and detail shapes.
  const raw = data as { theme?: unknown; aspects?: unknown };
  const theme = mapThemeRow(raw.theme);
  if (theme === null) return null;
  const parsed = ThemeDetailSchema.safeParse({
    theme,
    aspects: Array.isArray(raw.aspects) ? raw.aspects : [],
  });
  if (!parsed.success) {
    console.warn('[insights] theme detail parse failed', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

// --- get_game_channel_snapshots --------------------------------------------

function mapChannelSnapshotRow(raw: unknown): ChannelSnapshotStatus | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const camel = {
    discordChannelId: r['discord_channel_id'],
    channelName: r['channel_name'],
    lastDoneAt: r['last_done_at'],
    windowStart: r['window_start'],
    windowEnd: r['window_end'],
    aspectCount: r['aspect_count'],
    attachedCount: r['attached_count'],
    newThemeCount: r['new_theme_count'],
    inFlight: r['in_flight'],
  };
  const parsed = ChannelSnapshotStatusSchema.safeParse(camel);
  if (!parsed.success) {
    console.warn('[insights] channel snapshot row parse failed', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

export async function fetchChannelSnapshots(
  gameId: string,
): Promise<ChannelSnapshotStatus[]> {
  const { data, error } = await supabase.rpc('get_game_channel_snapshots', {
    p_game_id: gameId,
  });
  if (error !== null) {
    console.warn('[insights] get_game_channel_snapshots failed:', error.message);
    return [];
  }
  if (!Array.isArray(data)) return [];
  const rows: ChannelSnapshotStatus[] = [];
  for (const row of data) {
    const mapped = mapChannelSnapshotRow(row);
    if (mapped !== null) rows.push(mapped);
  }
  return rows;
}

// --- request_channel_snapshot (migration 0082) -----------------------------
//
// Post-0082 the RPC takes (channel, game) so two games sharing one Discord
// channel can each request an independent snapshot. The caller (Insights
// page) already has the active game in context.

export type RequestSnapshotError =
  | 'not_authenticated'
  | 'not_a_member'
  | 'channel_not_watched'
  | 'cooldown_active'
  | 'unknown';

export interface RequestSnapshotResult {
  ok: boolean;
  data: { snapshotId: string } | null;
  error: RequestSnapshotError | null;
}

const KNOWN_REQUEST_ERROR_TOKENS = new Set<RequestSnapshotError>([
  'not_authenticated',
  'not_a_member',
  'channel_not_watched',
  'cooldown_active',
]);

function classifyRequestError(message: string | undefined | null): RequestSnapshotError {
  if (typeof message !== 'string') return 'unknown';
  for (const token of KNOWN_REQUEST_ERROR_TOKENS) {
    if (message.includes(token)) return token;
  }
  return 'unknown';
}

export async function requestChannelSnapshot(
  discordChannelId: string,
  gameId: string,
): Promise<RequestSnapshotResult> {
  const { data, error } = await supabase.rpc('request_channel_snapshot', {
    p_discord_channel_id: discordChannelId,
    p_game_id: gameId,
  });
  if (error !== null) {
    console.warn('[insights] request_channel_snapshot failed:', error.message);
    return { ok: false, data: null, error: classifyRequestError(error.message) };
  }
  if (typeof data !== 'string') {
    return { ok: false, data: null, error: 'unknown' };
  }
  return { ok: true, data: { snapshotId: data }, error: null };
}

// --- request_non_discord_snapshot (migration 0102) -------------------------
//
// Game-scoped manual trigger for the non-Discord theme-formation pass.
// One in-flight non-Discord snapshot per game (partial unique index in
// migration 0102), idempotent on pending / claimed rows.
//
// Shares the RequestSnapshotResult envelope with requestChannelSnapshot
// so the Insights page can fan out both with one handler. The migration
// raises 'game_not_found' instead of 'channel_not_watched'; everything
// else (auth, membership) uses the same vocabulary.

export async function requestNonDiscordSnapshot(
  gameId: string,
): Promise<RequestSnapshotResult> {
  const { data, error } = await supabase.rpc('request_non_discord_snapshot', {
    p_game_id: gameId,
  });
  if (error !== null) {
    console.warn('[insights] request_non_discord_snapshot failed:', error.message);
    return { ok: false, data: null, error: classifyRequestError(error.message) };
  }
  if (typeof data !== 'string') {
    return { ok: false, data: null, error: 'unknown' };
  }
  return { ok: true, data: { snapshotId: data }, error: null };
}

// --- studio theme interventions (migration 0097) ---------------------------
//
// Three mutation RPCs the Insights surface uses to act on themes:
// detach a mention, dismiss a theme, merge two themes. All three share the
// same { ok, error } envelope; only merge_themes can return cross_studio_merge.

export type ThemeInterventionError =
  | 'not_authenticated'
  | 'not_a_member'
  | 'not_found'
  | 'invalid_state'
  | 'cross_studio_merge'
  | 'unknown';

export interface ThemeInterventionResult {
  ok: boolean;
  error: ThemeInterventionError | null;
}

const KNOWN_INTERVENTION_TOKENS = new Set<ThemeInterventionError>([
  'not_authenticated',
  'not_a_member',
  'not_found',
  'invalid_state',
  'cross_studio_merge',
]);

function classifyInterventionError(message: string | undefined | null): ThemeInterventionError {
  if (typeof message !== 'string') return 'unknown';
  for (const token of KNOWN_INTERVENTION_TOKENS) {
    if (message.includes(token)) return token;
  }
  return 'unknown';
}

export async function detachAspectFromTheme(
  themeId: string,
  aspectId: string,
): Promise<ThemeInterventionResult> {
  const { error } = await supabase.rpc('detach_aspect_from_theme', {
    p_theme_id: themeId,
    p_aspect_id: aspectId,
  });
  if (error !== null) {
    console.warn('[insights] detach_aspect_from_theme failed:', error.message);
    return { ok: false, error: classifyInterventionError(error.message) };
  }
  return { ok: true, error: null };
}

export async function dismissTheme(themeId: string): Promise<ThemeInterventionResult> {
  const { error } = await supabase.rpc('dismiss_theme', { p_theme_id: themeId });
  if (error !== null) {
    console.warn('[insights] dismiss_theme failed:', error.message);
    return { ok: false, error: classifyInterventionError(error.message) };
  }
  return { ok: true, error: null };
}

export async function mergeThemes(
  loserThemeId: string,
  winnerThemeId: string,
): Promise<ThemeInterventionResult> {
  const { error } = await supabase.rpc('merge_themes', {
    p_loser_theme_id: loserThemeId,
    p_winner_theme_id: winnerThemeId,
  });
  if (error !== null) {
    console.warn('[insights] merge_themes failed:', error.message);
    return { ok: false, error: classifyInterventionError(error.message) };
  }
  return { ok: true, error: null };
}

export async function restoreTheme(themeId: string): Promise<ThemeInterventionResult> {
  const { error } = await supabase.rpc('restore_theme', { p_theme_id: themeId });
  if (error !== null) {
    console.warn('[insights] restore_theme failed:', error.message);
    return { ok: false, error: classifyInterventionError(error.message) };
  }
  return { ok: true, error: null };
}
